import React, { useState } from 'react';
import {
  Box,
  Grid2 as Grid,
  Typography,
  useTheme,
  TextField,
  FormControl,
  MenuItem,
  Button,
  Stack,
  Chip,
  FormLabel,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import MailIcon from '@mui/icons-material/Mail';
import { ReactComponent as ArrowDown } from '../../../assets/icons/info/arrowDown.svg';
import { ReactComponent as HomeIcon } from '../../../assets/icons/info/home.svg';
import { ReactComponent as CheckInCircle } from '../../../assets/icons/info/checkInCircle.svg';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { contactFormSchema, ContactFormData } from './ContactFormData';
import { GlassColors } from '../../../components/glassmorphism/GlassmorphismStyles';
import { SecondaryButton } from '../../../components/GetStarted/GetStarted';
import { useNavigate } from 'react-router-dom';
import { http } from '../../../configuration/api/http';
import { ENV_WEB_API_BASEPATH } from '../../../configuration/api/apiHelper';

const topics = [
  'General Inquiry',
  'I am a Customer (Help with an Order)',
  'I am a Provider (Help with Task or Payout)',
  'Account & Verification Issue',
  'Report a Bug or Technical Issue',
  'Trust & Safety',
  'Refund Request',
  'Delete Account',
];
export const glassLabelSx = (isDark: boolean) => ({
  fontFamily: 'Roboto',
  fontSize: { xs: 14, sm: 16 },
  lineHeight: { xs: '143%', sm: '150%' },
  letterSpacing: { xs: '.17px', sm: '.15px' },
  color: isDark ? GlassColors.dark.textPrimary : GlassColors.light.textPrimary,
  display: 'block',
  marginBottom: '8px',
});

export const glassInputSx = (
  isDark: boolean,
  options?: { multiline?: boolean },
) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(132, 132, 132, 0.3)',
    borderRadius: '24px',
    lineHeight: { xs: '24px', sm: '29px' },
    border: 0,
    fontSize: { xs: 12, sm: 14 },
    fontFamily: '"Montserrat", sans-serif',
    boxShadow:
      '0px 0px 10px 0px rgba(0, 0, 0, 0.04), 0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3), 0px 0.5px 0.75px 0px rgba(255, 255, 255, 1), 0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset, 0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset',
    ...(options?.multiline
      ? {
          height: 'auto',
          padding: '12px 24px',
          alignItems: 'flex-start',
        }
      : {
          height: 36,
          padding: { xs: '6px 16px', sm: '3.5px 16px' },
        }),
  },
  color: isDark ? GlassColors.dark.textPrimary : GlassColors.light.textPrimary,
  '& .MuiInputBase-input::placeholder': {
    color: isDark
      ? GlassColors.dark.textSecondary
      : GlassColors.light.textSecondary,
    opacity: 1,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #c8c8c2 inset',
    WebkitTextFillColor: '#1A1A1A',
  },
  '& input:-webkit-autofill:focus': {
    WebkitBoxShadow: '0 0 0 1000px #c8c8c2 inset',
  },
});

export const ContactUsForm = () => {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting, isSubmitSuccessful, isValid },
    reset,
  } = useForm<ContactFormData>({
    mode: 'onChange',
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      fullName: '',
      email: '',
      topic: '',
      message: '',
      attachments: [],
    },
  });

  const attachments = useWatch({
    control,
    name: 'attachments',
  });
  const { append, remove } = useFieldArray({
    control,
    name: 'attachments',
  });

  const onSubmit = async (data: ContactFormData) => {
    const url = ENV_WEB_API_BASEPATH + '/Zendesk/tickets';

    const payload = {
      subject: data.topic,
      comment: data.message,
      requesterEmail: data.email,
      requesterName: data.fullName,
      attachmentTokens: attachments.map((att) => att.token),
    };

    const res = await http(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      console.log(`HTTP ${res.status}: ${text}`);
    }

    // await new Promise((r) => setTimeout(r, 1000));
    // console.log('done');
    reset();
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isFileUploading, setIsFileUploading] = useState(false);
  let isFilePickDisabled =
    isSubmitting || isFileUploading || attachments.length >= 5;
  let isSubmitDisabled = isSubmitting || isFileUploading || !isValid;

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file, file.name);

    setIsFileUploading(true);
    const res = await http(ENV_WEB_API_BASEPATH + '/Zendesk/attachments', {
      method: 'POST',
      headers: {
        accept: 'text/plain',
      },
      body: formData,
    });

    const resp = await res.json();
    setIsFileUploading(false);

    append({
      name: resp?.upload?.attachment?.file_name,
      token: resp?.upload?.token,
    });

    e.target.value = '';
  };

  const navigate = useNavigate();

  return !isSubmitSuccessful ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        maxWidth: 760,
        margin: 'auto',
        padding: '24px 24px 32px',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '-2.82px -2.82px 8.49px 0px #FFFFFF4D, 14.15px 14.15px 22.64px -0.37px #0000001A, 1.58px 1.58px 1.7px 0px #FFFFFF inset, -2.07px -2.64px 4.52px -1.51px #0000004D inset',
        borderRadius: '32px',
      }}
    >
      <Box>
        <Typography
          variant='h5'
          sx={{
            textAlign: 'center',
            fontWeight: { xs: 800, sm: 700 },
            fontFamily: '"Montserrat", sans-serif',
            fontSize: { xs: 20, sm: 24 },
            lineHeight: { xs: '160%', sm: '133%' },
            color: isDark ? '#00FFFF' : '#005CAD',
            marginBottom: '8px',
          }}
        >
          Contact Us
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Roboto',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16 },
            lineHeight: { xs: '157%', sm: '175%' },
            color: isDark
              ? GlassColors.dark.textPrimary
              : GlassColors.light.textSecondary,
            letterSpacing: '.1px',
          }}
        >
          Have a question, issue, or feedback? Fill out the form below, and our
          team will get back to you within 24 hours.
        </Typography>
      </Box>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ maxWidth: '100%' }}
      >
        <Grid container spacing={2}>
          {/* Full name field */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormLabel sx={glassLabelSx(isDark)} htmlFor='fullName'>
              Full Name*
            </FormLabel>

            <TextField
              placeholder='e.g. John Smith'
              sx={glassInputSx(isDark)}
              id='fullName'
              fullWidth
              {...register('fullName')}
              error={!!errors.fullName}
              helperText={errors.fullName?.message}
            />
          </Grid>

          {/* Email address field */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormLabel htmlFor='email' sx={glassLabelSx(isDark)}>
              Email Address*
            </FormLabel>
            <TextField
              id='email'
              placeholder='name@example.com'
              sx={glassInputSx(isDark)}
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              fullWidth
            />
          </Grid>

          {/* Select topic field */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <FormLabel id='topic-label' sx={glassLabelSx(isDark)}>
                What can we help you with?*
              </FormLabel>

              <TextField
                select
                fullWidth
                defaultValue=''
                {...register('topic')}
                error={!!errors.topic}
                helperText={errors.topic?.message}
                sx={{
                  '&': {
                    borderRadius: '24px',
                  },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(132, 132, 132, 0.2)',
                    borderRadius: '24px',
                    fontFamily: '"Montserrat", sans-serif',
                    lineHeight: { xs: '24px', sm: '29px' },
                    border: 0,
                    height: { xs: '40px', sm: '53px' },
                    padding: '0 24px',
                    fontSize: { xs: 12, sm: 14 },
                    boxShadow:
                      '0px 0px 34.77px 0px #0000000D inset, 0px -0.5px 0.25px 0px #00000080 inset, 0px 0.5px 0.75px 0px #FFFFFF inset, 0px 2.07px 3.73px 0px #0000004D, 0px 2.08px 2.48px -2px #00000066, 0px 3.08px 2px -2px #00000054',
                  },
                  '& .Mui-focused': {
                    outline: 'none',
                  },
                  '& ul svg': { right: { xs: '8px', sm: '16px' } },
                  '& ul': {
                    padding: { xs: '8px 0', sm: '12px 0' },
                  },
                  '& svg': {
                    top: { xs: '8px', sm: '14.5px' },
                    right: { xs: '24px' },
                    '& path': {
                      fill: isDark
                        ? GlassColors.dark.textSecondary
                        : GlassColors.light.textSecondary,
                    },
                  },
                  '& .MuiModal-root.MuiPopover-root.MuiMenu-root, & .MuiBackdrop-root-.MuiModal-backdrop':
                    {
                      position: 'absolute',
                      zIndex: 1,
                    },
                }}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    IconComponent: ArrowDown,
                    MenuProps: {
                      disableScrollLock: true,
                      disablePortal: true,
                      marginThreshold: -9999,
                      TransitionComponent: Collapse,
                      TransitionProps: {
                        timeout: 200,
                      },
                      PaperProps: {
                        sx: {
                          '&': {
                            background: isDark ? '#2F2F2F ' : '#D1D1CA ',
                            boxShadow:
                              '0px 0px 34.77px 0px #0000000D inset, 0px -0.5px 0.25px 0px #00000080 inset, 0px 0.5px 0.75px 0px #FFFFFF inset, 0px 2.07px 3.73px 0px #0000004D, 0px 2.08px 2.48px -2px #00000066, 0px 3.08px 2px -2px #00000054',
                          },
                          '& .MuiSelect-select': {
                            height: '100%',
                          },
                          borderRadius: { xs: '19px', sm: '24px' },
                          lineHeight: { xs: '24px', sm: '29px' },
                          border: 0,
                          marginTop: '0',
                          maxHeight: 'unset',
                          top: '0!important',
                          left: '0!important',
                          padding: { xs: '0 16px', sm: '0 24px' },
                          position: 'relative',
                          fontSize: { xs: 12, sm: 14 },
                          boxShadow:
                            '0px 0px 34.77px 0px #0000000D inset, 0px -0.5px 0.25px 0px #00000080 inset, 0px 0.5px 0.75px 0px #FFFFFF inset, 0px 2.07px 3.73px 0px #0000004D, 0px 2.08px 2.48px -2px #00000066, 0px 3.08px 2px -2px #00000054',
                          '& svg': {
                            position: 'absolute',
                            transform: 'rotate(180deg)',
                            right: 0,
                            '& path': {
                              fill: isDark
                                ? GlassColors.dark.textSecondary
                                : GlassColors.light.textSecondary,
                            },
                          },
                        },
                      },
                      anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    },
                    renderValue: (selected) =>
                      selected ? (
                        (selected as string)
                      ) : (
                        <Typography
                          component='span'
                          sx={{
                            fontFamily: '"Montserrat", sans-serif',
                            fontSize: { xs: 12, sm: 14 },
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            color: isDark
                              ? GlassColors.dark.textSecondary
                              : GlassColors.light.textSecondary,
                            '&': {
                              display: 'block',
                            },
                          }}
                        >
                          Select a topic
                        </Typography>
                      ),
                    'aria-labelledby': 'topic-label',
                  },
                  input: {
                    id: 'topic',
                    'aria-labelledby': 'topic-label',
                  },
                }}
              >
                <ArrowDown />
                <MenuItem
                  disabled
                  value=''
                  sx={{
                    opacity: '1!important',
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '12px', sm: '14px' },
                    lineHeight: '28.8px',
                    minHeight: 'unset',
                    textWrap: 'auto',
                    padding: 0,
                    marginBottom: '8px',
                    '&.Mui-selected': {
                      background: 'transparent!important',
                    },
                  }}
                >
                  Select a topic
                </MenuItem>

                {topics.map((topic) => (
                  <MenuItem
                    key={topic}
                    value={topic}
                    sx={{
                      opacity: '1!important',
                      fontFamily: '"Montserrat", sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '12px', sm: '14px' },
                      lineHeight: { xs: '24px', sm: '28.8px' },
                      minHeight: 'unset',
                      textWrap: 'auto',
                      padding: '0 16px',
                      marginBottom: '8px',
                      '&.Mui-selected': {
                        background: 'transparent',
                      },
                    }}
                  >
                    {topic}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>

          {/* Message field */}
          <Grid size={{ xs: 12 }}>
            <FormLabel htmlFor='message' sx={glassLabelSx(isDark)}>
              Message*
            </FormLabel>
            <TextField
              id='message'
              placeholder='Please describe your issue in detail. The more info you provide, the faster we can help.'
              sx={glassInputSx(isDark, { multiline: true })}
              fullWidth
              {...register('message')}
              error={!!errors.message}
              helperText={errors.message?.message}
              multiline
              minRows={3}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={glassLabelSx(isDark)}>
              Attachments (Optional)
            </Typography>

            {/* Upload field */}
            <Button
              component='label'
              disabled={isFilePickDisabled}
              sx={{
                border: isDark
                  ? `2px dashed ${GlassColors.dark.textSecondary}`
                  : `2px dashed ${GlassColors.light.textPrimary}`,
                borderRadius: '24px',
                p: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '64px',
                opacity: 1,
                fontFamily: 'Roboto',
                fontSize: { xs: 14, sm: 16 },
                lineHeight: { xs: '143%', sm: '150%' },
                letterSpacing: { xs: '.17px', sm: '.15px' },
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
                transition: 'opacity easy .3s',

                '&:hover': {
                  backgroundColor: 'unset',
                  opacity: '0.7',
                },
              }}
            >
              Upload screenshots or documents
              <input
                hidden
                type='file'
                onChange={onPickFiles}
                // accept="image/*,.pdf,.doc,.docx"
              />
            </Button>

            {/* Attachments list */}
            {!!attachments.length && (
              <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 1.5 }}>
                {attachments.map((att, idx) => (
                  <Chip
                    deleteIcon={<CloseIcon />}
                    label={att.name}
                    key={`${att.name}-${idx}`}
                    onDelete={() => remove(idx)}
                  ></Chip>
                ))}
              </Stack>
            )}
          </Grid>

          <Grid
            size={{ xs: 12 }}
            sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
          >
            <SecondaryButton
              type='submit'
              disabled={isSubmitDisabled}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                color: '#fff',
                fontSize: { xs: 12, sm: 16 },
                lineHeight: { xs: '32px', sm: '32px' },
                height: '48px',
                padding: '8px 24px',
                borderRadius: '24px',
                backgroundColor: isDark
                  ? GlassColors.dark.iconBtnBackground
                  : GlassColors.light.iconBtnBackground,
              }}
            >
              <MailIcon />
              Send message
            </SecondaryButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        maxWidth: 760,
        margin: 'auto',
        padding: '24px 32px',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '-2.82px -2.82px 8.49px 0px #FFFFFF4D, 14.15px 14.15px 22.64px -0.37px #0000001A, 1.58px 1.58px 1.7px 0px #FFFFFF inset, -2.07px -2.64px 4.52px -1.51px #0000004D inset',
        borderRadius: '32px',
      }}
    >
      <Typography
        variant='h5'
        sx={{
          textAlign: 'center',
          fontWeight: { xs: 800, sm: 700 },
          fontFamily: 'Montserrat',
          fontSize: { xs: 24, sm: 48 },
          lineHeight: { xs: '160%', sm: '133%' },
          color: isDark ? '#00FFFF' : '#005CAD',
          marginBottom: '8px',
        }}
      >
        Message Sent!
      </Typography>
      <CheckInCircle />
      <Typography
        sx={{
          fontFamily: 'Roboto',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: { xs: 14, sm: 16 },
          lineHeight: { xs: '157%', sm: '175%' },
          color: isDark
            ? GlassColors.dark.textPrimary
            : GlassColors.light.textSecondary,
          letterSpacing: '.1px',
        }}
      >
        Thanks for reaching out. We've received your request and will send a
        reply to your email shortly.
      </Typography>
      <SecondaryButton onClick={() => navigate('/')}>
        <HomeIcon /> Back to home
      </SecondaryButton>
    </Box>
  );
};
